import { useState, useEffect } from "react";
import { OledIrisEyes, OledOfficialEyes } from "./chunk-13.jsx";
import { OledPixelEyes } from "./chunk-14.jsx";
export function pickOledProfile(variant) {
  switch (variant) {
    // Open-source inspired variants (algorithmic, no assets)
    case 'ir_basic':
      return {
        mode: 'iris',
        gap: 12,
        eyeRadius: 0.56,
        pupil: 0.38,
        outline: true,
        pupilShape: 'round',
        gaze: 'oledGaze'
      };
    case 'iris_ring':
      return {
        mode: 'iris',
        gap: 14,
        eyeRadius: 0.62,
        pupil: 0.28,
        outline: true,
        irisRing: true,
        pupilShape: 'round',
        pupilPulse: true,
        gaze: 'oledGazeWide'
      };
    case 'iris_cat':
      return {
        mode: 'iris',
        gap: 14,
        eyeRadius: 0.6,
        pupil: 0.3,
        outline: true,
        iris: '#f97316',
        pupilShape: 'slit',
        pupilPulse: true,
        gaze: 'oledGazeWide'
      };
    case 'iris_star':
      return {
        mode: 'iris',
        gap: 14,
        eyeRadius: 0.6,
        pupil: 0.32,
        outline: true,
        iris: '#60a5fa',
        pupilShape: 'star',
        pupilPulse: true,
        gaze: 'oledGazeJitter'
      };
    case 'ojos_pro':
      return {
        mode: 'iris',
        gap: 16,
        eyeRadius: 0.66,
        pupil: 0.26,
        outline: true,
        outlineWidth: 3,
        irisRing: true,
        iris: '#34d399',
        pupilShape: 'round',
        pupilPulse: true,
        scanline: true,
        gaze: 'oledGazeWide'
      };
    case 'dragon':
      return {
        mode: 'iris',
        gap: 12,
        eyeRadius: 0.7,
        pupil: 0.24,
        outline: true,
        outlineWidth: 3,
        iris: '#ef4444',
        pupilShape: 'slit',
        scanline: true,
        gaze: 'oledGazeJitter'
      };
    case 'robot_eyes':
    case 'robotic_eye':
    case 'oled_robot_eye':
    case 'robot_eyes_oled':
      return {
        mode: 'pixel',
        gap: 12,
        cols: 12,
        rows: 7,
        pixelSize: 7,
        on: 'bg-white/95',
        scanline: true,
        gaze: 'oledGazeTiny'
      };
    case 'matrix':
      return {
        mode: 'pixel',
        gap: 12,
        cols: 14,
        rows: 8,
        pixelSize: 6,
        on: 'bg-emerald-300/90',
        scanline: true,
        gaze: 'oledGazeTiny'
      };
    case 'soft_mono':
      return {
        mode: 'soft',
        gap: 12,
        eyeRadius: 0.36,
        pupil: 0.22,
        outline: false,
        iris: '#111827',
        gaze: 'oledGaze'
      };
    case 'soft_thick':
      return {
        mode: 'soft',
        gap: 14,
        eyeRadius: 0.52,
        pupil: 0.24,
        outline: true,
        outlineWidth: 3,
        iris: '#111827',
        gaze: 'oledGazeWide'
      };
    case 'official_big':
      return {
        mode: 'official',
        gap: 12,
        eyeBoxW: 0.32,
        eyeBoxH: 0.62,
        gaze: 'oledGazeWide',
        scanline: true
      };
    case 'official_tight':
      return {
        mode: 'official',
        gap: 10,
        eyeBoxW: 0.26,
        eyeBoxH: 0.54,
        gaze: 'oledGazeTiny'
      };
    case 'iris':
      return {
        mode: 'iris',
        gap: 14,
        eyeRadius: 0.58,
        pupil: 0.42,
        outline: true,
        pupilShape: 'round',
        gaze: 'oledGaze'
      };
    case 'robotics':
      return {
        mode: 'pixel',
        gap: 12,
        cols: 10,
        rows: 6,
        pixelSize: 8,
        on: 'bg-white/95',
        scanline: true,
        gaze: 'oledGazeTiny'
      };
    case 's3':
      return {
        mode: 'soft',
        gap: 10,
        eyeRadius: 0.32,
        pupil: 0.3,
        outline: false,
        pupilShape: 'round',
        gaze: 'oledGazeWide'
      };
    case 'lenny':
      return {
        mode: 'soft',
        gap: 16,
        eyeRadius: 0.48,
        pupil: 0.28,
        outline: true,
        pupilShape: 'round',
        gaze: 'oledGaze'
      };
    default:
      return {
        mode: 'official',
        gap: 14,
        eyeBoxW: 0.28,
        eyeBoxH: 0.58,
        gaze: 'oledGaze'
      };
  }
}
export function OledEyes({
  emotion,
  variant,
  poke = 0
}) {
  const profile = pickOledProfile(variant);
  const [blinkPulse, setBlinkPulse] = useState(false);
  useEffect(() => {
    if (!poke) return;
    setBlinkPulse(true);
    const t = setTimeout(() => setBlinkPulse(false), 240);
    return () => clearTimeout(t);
  }, [poke]);
  const keyframes = `
    @keyframes oledBlink{0%,92%,100%{transform:scaleY(0)}94%{transform:scaleY(1)}96%{transform:scaleY(0)}}
    @keyframes oledGaze{0%,100%{transform:translate3d(0,0,0)}22%{transform:translate3d(-7%,3%,0)}45%{transform:translate3d(6%,-2%,0)}70%{transform:translate3d(-3%,-6%,0)}}
    @keyframes oledGazeWide{0%,100%{transform:translate3d(0,0,0)}20%{transform:translate3d(-11%,5%,0)}46%{transform:translate3d(10%,-4%,0)}74%{transform:translate3d(-6%,-10%,0)}}
    @keyframes oledGazeTiny{0%,100%{transform:translate3d(0,0,0)}28%{transform:translate3d(-4%,2%,0)}58%{transform:translate3d(4%,-2%,0)}80%{transform:translate3d(-2%,-4%,0)}}
    @keyframes oledGazeJitter{0%,100%{transform:translate3d(0,0,0)}18%{transform:translate3d(-8%,4%,0)}20%{transform:translate3d(-6%,2%,0)}44%{transform:translate3d(7%,-3%,0)}46%{transform:translate3d(5%,-1%,0)}70%{transform:translate3d(-4%,-7%,0)}72%{transform:translate3d(-2%,-6%,0)}}
    @keyframes oledPupilPulse{0%,100%{transform:translate(-50%,-50%) scale(1)}50%{transform:translate(-50%,-50%) scale(0.86)}}
    @keyframes oledScanY{0%{transform:translateY(-120%)}100%{transform:translateY(120%)}}
  `;
  const render = (() => {
    if (profile.mode === 'iris' || profile.mode === 'soft') {
      return <OledIrisEyes emotion={emotion} profile={profile} blinkPulse={blinkPulse} />;
    }
    if (profile.mode === 'pixel') {
      return <OledPixelEyes emotion={emotion} profile={profile} blinkPulse={blinkPulse} />;
    }
    return <OledOfficialEyes emotion={emotion} profile={profile} blinkPulse={blinkPulse} />;
  })();
  return <div className="relative w-full h-full">
      {render}
      {profile.scanline ? <div className="pointer-events-none absolute inset-0 opacity-[0.10]" style={{
      backgroundImage: 'linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,0.10) 45%, rgba(255,255,255,0) 100%)',
      animation: 'oledScanY 1.8s linear infinite'
    }} /> : null}
      <style>{keyframes}</style>
    </div>;
}
